import { Grid } from "@mui/material";
import { useMemo } from "react";
import { getRealm } from "../../../../../AuthProvider";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import ReadOnlyFields from "../../../../../components/ReadOnlyFields";
import { useQueryParams } from "../../../../../hooks/use-query-param";
import { usePatientIntakeFormList } from "../../../../../queries/patient-portal/forms/use-patient-intake-form-list";
import { DEF_PAGINATION } from "../../../../../utils/utils";
import { IntakeFormSection } from "../appointment/encounter/content-section/intake-preview/IntakePreviewModal";
import PatientHippaConsent from "./intake-form-views/PatientHippaConsent";
import PatientBasicDetails from "./intake-form-views/PatientBasicDetails";
import PatientInsuranceDetails from "./intake-form-views/PatientInsuranceDetails";
import PatientMedicalHistory from "./intake-form-views/PatientMedicalHistory";
import PatientPaymentPolicies from "./intake-form-views/PatientPaymentPolicies";
import PatientPrescriptionPolicy from "./intake-form-views/PatientPrescriptionPolicy";
import PatientMedicalServicesAgreement from "./intake-form-views/PatientMedicalServicesAgreement";
import PatientPhysicianPatientArbitrationAgreement from "./intake-form-views/PatientPhysicianPatientArbitrationAgreement";
import { initialValues } from "./new-intake-form-contant/intake-constant";
import IndividualPsychotherapy from "./IndividualPsychotherapy";
import ClientInformationForm from "./ClinicalHistoryFormComponents/ClientInformationForm";
import ClientInsuranceForm from "./ClinicalHistoryFormComponents/ClientInsuranceForm";
import ClientHistoryForm from "./ClinicalHistoryFormComponents/ClientHistoryForm";
import ConsentForServicesForm from "./ClinicalHistoryFormComponents/ConsentForServicesForm";
import AIConsentForm from "./ClinicalHistoryFormComponents/AIConsentForm";
import PrivacyPracticesForm from "./ClinicalHistoryFormComponents/PrivacyPracticesForm";
import { useGetProviderGroupPracticeInfo } from "@/queries/use-get-provider-group-practice-info";
import ReleaseOfInformationForm from "./ClinicalHistoryFormComponents/ReleaseOfInformationForm";

interface ViewPatientIntakeFormProps {
  isPublic?: boolean;
  prefillData?: any;
  showOnlyIntakeForms?: IntakeFormSection[];
}

const ViewPatientIntakeForm = ({
  isPublic,
  prefillData,
  showOnlyIntakeForms,
}: ViewPatientIntakeFormProps) => {
  const appointmentUuid = useQueryParams("appointmentId");
  const encounterAppointmentId = useQueryParams("appointmentid");
  const hostNameArr = location.hostname?.split(".");
  const xTENANTID = getRealm(hostNameArr);
  const {
    data: prefillIntakeForm,
    isRefetching,
    isLoading,
  } = usePatientIntakeFormList({
    appointmentUuid: appointmentUuid ?? encounterAppointmentId ?? undefined,
    isPublic,
    pagination: DEF_PAGINATION,
    xTENANTID,
  });

  const { data: practiceInfo } = useGetProviderGroupPracticeInfo({
    xTENANTID,
    isPublic,
  });

  const intakeformParam = useQueryParams("intakeform");

  const prefillIntakeFormData =
    prefillData?.content || prefillIntakeForm?.data?.content[0]?.content;

  const intakeForms = showOnlyIntakeForms?.length
    ? showOnlyIntakeForms
    : (intakeformParam?.split(",") ??
      prefillData?.activeSections ??
      prefillIntakeForm?.data?.content[0]?.activeSections?.split(","));

  // Parse values from prefillIntakeFormData or use initialValues
  const values = useMemo(() => {
    if (prefillIntakeFormData) {
      try {
        return JSON.parse(prefillIntakeFormData);
      } catch {
        return initialValues;
      }
    }
    return initialValues;
  }, [prefillIntakeFormData]);

  return (
    <ReadOnlyFields>
      <LoadingOverlay isLoading={isLoading && isRefetching} />
      <Grid container spacing={2}>
        {(intakeForms?.includes("demographics") ||
          intakeForms?.includes("Demographics")) && (
          <Grid size={12}>
            <PatientBasicDetails values={values} />
          </Grid>
        )}

        {(intakeForms?.includes("insurance") ||
          intakeForms?.includes("Insurance")) && (
          <Grid size={12}>
            <PatientInsuranceDetails values={values} />
          </Grid>
        )}

        {(intakeForms?.includes("paymentPolicy") ||
          intakeForms?.includes("Payment Policy")) && (
          <Grid size={12}>
            <PatientPaymentPolicies values={values} />
          </Grid>
        )}

        {(intakeForms?.includes("prescription") ||
          intakeForms?.includes("Prescription")) && (
          <Grid size={12}>
            <PatientPrescriptionPolicy values={values} />
          </Grid>
        )}

        {(intakeForms?.includes("patientMedicalHistory") ||
          intakeForms?.includes("Patient Medical History")) && (
          <Grid size={12}>
            <PatientMedicalHistory values={values} />
          </Grid>
        )}

        <Grid container size={12}>
          <Grid size={12}>
            {(intakeForms?.includes("hippaConsent") ||
              intakeForms?.includes("HIPAA Consent") ||
              intakeForms?.includes("HIPAAConsent")) && (
              <PatientHippaConsent values={values} />
            )}
          </Grid>
          <Grid size={12}>
            {(intakeForms?.includes("medicalServicesAgreement") ||
              intakeForms?.includes("Medical Services Agreement")) && (
              <PatientMedicalServicesAgreement values={values} />
            )}
          </Grid>
          <Grid size={12}>
            {(intakeForms?.includes("physicianPatientArbitrationAgreement") ||
              intakeForms?.includes(
                "Physician Patient Arbitration Agreement"
              )) && (
              <PatientPhysicianPatientArbitrationAgreement values={values} />
            )}
          </Grid>
          <Grid size={12}>
            {(intakeForms?.includes("Psychoeducation Therapy") ||
              intakeForms?.includes("PSYCHOEDUCATION_THERAPY")) && (
              <IndividualPsychotherapy
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            )}
          </Grid>
          <Grid size={12}>
            {(intakeForms?.includes("Individual Psychotherapy") ||
              intakeForms?.includes("INDIVIDUAL_PSYCHOTHERAPY")) && (
              <ClientInformationForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            )}
          </Grid>
          <Grid size={12}>
            {(intakeForms?.includes("clientInformationForm") ||
              intakeForms?.includes("Client Information Form")) && (
              <ClientInformationForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            )}
          </Grid>
          {(intakeForms?.includes("clientHistoryForm") ||
            intakeForms?.includes("Client History Form")) && (
            <Grid size={12}>
              <ClientHistoryForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            </Grid>
          )}
          {(intakeForms?.includes("clientInsuranceForm") ||
            intakeForms?.includes("Client Insurance Form")) && (
            <Grid size={12}>
              <ClientInsuranceForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            </Grid>
          )}
          {(intakeForms?.includes("consentForServices") ||
            intakeForms?.includes("Consent For Services")) && (
            <Grid size={12}>
              <ConsentForServicesForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            </Grid>
          )}
          {(intakeForms?.includes("aiConsent") ||
            intakeForms?.includes(
              "Consent For Use Of Artificial Intelligence"
            )) && (
            <Grid size={12}>
              <AIConsentForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            </Grid>
          )}
          {(intakeForms?.includes("privacyPractices") ||
            intakeForms?.includes("Notice Of Privacy Practices")) && (
            <Grid size={12}>
              <PrivacyPracticesForm
                practiceInfo={practiceInfo}
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            </Grid>
          )}
          {(intakeForms?.includes("releaseOfInformation") ||
            intakeForms?.includes("Release Of Information")) && (
            <Grid size={12}>
              <ReleaseOfInformationForm
                values={values}
                setFieldValue={() => {}}
                handleChange={() => {}}
                handleBlur={() => {}}
                touched={{}}
                errors={{}}
                viewOnly
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </ReadOnlyFields>
  );
};

export default ViewPatientIntakeForm;
